/**
 * Super skills (the owner, 22/10/2026; docs/refactor/50): skills used by a button, for people who are not technical.
 * A grid of big buttons; each opens its own menu — plain options first, the skill's own behind "Avançado", which skill
 * it uses and whether this computer has it — and "Usar" sends the catalog's prompt filled with the choices, the prompt
 * box showing the person's words. A button the chosen AI cannot do stays off, with why and where to change it.
 */

import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowLeft, Clapperboard, Copy, FileSearch, FileText, FolderOpen, Globe, Languages, Palette, Presentation, Search, Sheet, Sparkles,
} from 'lucide-react'
import { useI18n } from '../lib/i18n'
import type { SuperSkill, SuperSkillField } from '../lib/types'
import { canRun, fillPrompt, initialValues, installedHere, local, MAX_FIELD, missing, shownText, visible, type SuperSkillValues } from '../lib/superSkills'
import { Modal } from './Modal'

/** A team super skill read once on this computer: its instructions stay folded from then on (SEC2-13). */
const SEEN = 'vephus.superSkills.seen.'
function seenHere(key: string): boolean {
  try {
    return window.localStorage.getItem(SEEN + key) === '1'
  } catch {
    return false
  }
}
function markSeen(key: string): void {
  try {
    window.localStorage.setItem(SEEN + key, '1')
  } catch {
    // Private window: it opens again next time, which is the safe side.
  }
}

const ICONS: Record<string, ReactNode> = {
  search: <Search size={20} />,
  'file-text': <FileText size={20} />,
  'file-search': <FileSearch size={20} />,
  clapperboard: <Clapperboard size={20} />,
  palette: <Palette size={20} />,
  presentation: <Presentation size={20} />,
  sheet: <Sheet size={20} />,
  globe: <Globe size={20} />,
  languages: <Languages size={20} />,
  copy: <Copy size={20} />,
}

/** A super skill's icon, by the catalog's name for it. */
export function superSkillIcon(icon: string): ReactNode {
  return ICONS[icon] ?? <Sparkles size={20} />
}

export function SuperSkills({
  skills,
  promptFor,
  agent,
  agentLabel,
  desktop,
  here,
  pickFile,
  onSend,
  onClose,
  initial,
}: {
  skills: SuperSkill[]
  /** The catalog prompt's text for a key; null while the catalog has not come. */
  promptFor: (key: string) => string | null
  /** The agent that would do it on this computer (claude, codex…); null on the web. */
  agent: string | null
  agentLabel: string
  desktop: boolean
  /** The skills on this computer; null when nobody here can tell (the web). */
  here: Array<{ name: string; enabled: boolean }> | null
  pickFile: (extensions: string[]) => Promise<string | null>
  onSend: (text: string, shown: string) => void
  onClose: () => void
  initial?: string | null
}) {
  const { t, locale } = useI18n()
  const [open, setOpen] = useState<SuperSkill | null>(() => skills.find((s) => s.key === initial) ?? null)

  return (
    <Modal title={open ? local(open.title, locale) : t('superSkills.title')} onClose={onClose} wide>
      {open ? (
        <SuperSkillMenu
          skill={open}
          body={promptFor(open.prompt)}
          able={canRun(open, agent, desktop)}
          agentLabel={agentLabel}
          desktop={desktop}
          installed={installedHere(open, here)}
          pickFile={pickFile}
          onBack={() => setOpen(null)}
          onSend={(text, shown) => {
            onSend(text, shown)
            onClose()
          }}
        />
      ) : (
        <div className="stack">
          <p className="small muted" style={{ margin: 0 }}>{t('superSkills.intro')}</p>
          <div className="super-skills__grid">
            {skills.map((skill) => {
              const able = canRun(skill, agent, desktop)
              return (
                <button
                  key={skill.key}
                  type="button"
                  className={`super-skills__card${able ? '' : ' is-off'}`}
                  onClick={() => setOpen(skill)}
                  title={able ? undefined : t('superSkills.cannot', { agent: agentLabel })}
                >
                  <span className="super-skills__icon" aria-hidden>{superSkillIcon(skill.icon)}</span>
                  <span className="super-skills__name">{local(skill.title, locale)}</span>
                  {skill.team && <span className="super-skills__team tiny" title={skill.team}>{t('superSkills.team')}</span>}
                  <span className="tiny muted">{local(skill.description, locale)}</span>
                </button>
              )
            })}
          </div>
          <p className="tiny muted" style={{ margin: 0 }}>
            {t('superSkills.more')} <Link to="/settings/skills" onClick={onClose}>{t('superSkills.manage')}</Link>
          </p>
        </div>
      )}
    </Modal>
  )
}

function SuperSkillMenu({
  skill,
  body,
  able,
  agentLabel,
  desktop,
  installed,
  pickFile,
  onBack,
  onSend,
}: {
  skill: SuperSkill
  body: string | null
  able: boolean
  agentLabel: string
  desktop: boolean
  installed: boolean | null
  pickFile: (extensions: string[]) => Promise<string | null>
  onBack: () => void
  onSend: (text: string, shown: string) => void
}) {
  const { t, locale } = useI18n()
  const [values, setValues] = useState<SuperSkillValues>(() => initialValues(skill))
  const [updateSkill, setUpdateSkill] = useState(false)
  const [tried, setTried] = useState(false)
  const set = (key: string, value: string) => setValues((current) => ({ ...current, [key]: value }))
  const lacking = missing(skill, values)
  const plain = skill.fields.filter((f) => !f.advanced && visible(f, values))
  const advanced = skill.fields.filter((f) => f.advanced && visible(f, values))

  const use = () => {
    setTried(true)
    if (!body || !able || lacking.length > 0) return
    if (skill.team) markSeen(skill.key)
    onSend(fillPrompt(body, skill, values, { locale, installed, updateSkill }), shownText(skill, values, locale))
  }

  return (
    <div className="stack super-skill">
      <button type="button" className="btn btn--ghost btn--sm super-skill__back" onClick={onBack}>
        <ArrowLeft size={13} aria-hidden /> {t('superSkills.back')}
      </button>
      <p className="small muted" style={{ margin: 0 }}>{local(skill.description, locale)}</p>
      {!able && (
        <div className="notice small notice--warn" role="status">
          {t('superSkills.cannot', { agent: agentLabel })} <Link to="/settings/connect">{t('superSkills.chooseAgent')}</Link>
        </div>
      )}

      {plain.map((field) => (
        <Field key={field.key} field={field} value={values[field.key] ?? ''} onChange={(v) => set(field.key, v)} desktop={desktop} pickFile={pickFile} invalid={tried && lacking.includes(field.key)} />
      ))}

      {(skill.skill || advanced.length > 0) && (
        <details className="super-skill__advanced">
          <summary className="small">{t('superSkills.advanced')}</summary>
          <div className="stack stack--tight">
            {advanced.map((field) => (
              <Field key={field.key} field={field} value={values[field.key] ?? ''} onChange={(v) => set(field.key, v)} desktop={desktop} pickFile={pickFile} invalid={tried && lacking.includes(field.key)} />
            ))}
            {skill.skill && (
              <label className="row row--tight small">
                <input type="checkbox" checked={updateSkill} onChange={(e) => setUpdateSkill(e.target.checked)} />
                {t('superSkills.updateSkill')}
              </label>
            )}
          </div>
        </details>
      )}

      {skill.skill && (
        <p className="tiny muted" style={{ margin: 0 }}>
          {t('superSkills.uses')} <a href={skill.skill.repo} target="_blank" rel="noreferrer">{skill.skill.name}</a>
          {' · '}
          {installed === true ? t('superSkills.installed') : installed === false ? t('superSkills.notInstalled') : t('superSkills.installedThere')}
        </p>
      )}
      {skill.team && body && (
        // A colleague wrote these instructions: the person reads what goes to the AI before it runs (SEC2-13).
        <details className="super-skill__advanced" open={!seenHere(skill.key)}>
          <summary className="small">{t('superSkills.teamPrompt', { team: skill.team })}</summary>
          <pre className="super-skill__prompt tiny">{body}</pre>
        </details>
      )}
      {skill.note && <p className="tiny muted" style={{ margin: 0 }}>{local(skill.note, locale)}</p>}
      {!desktop && <p className="tiny muted" style={{ margin: 0 }}>{t('superSkills.onTheWeb')}</p>}
      {tried && lacking.length > 0 && <p className="tiny field__error" role="alert" style={{ margin: 0 }}>{t('superSkills.missing')}</p>}
      {!body && <p className="tiny field__error" role="alert" style={{ margin: 0 }}>{t('composer.catalogMissing')}</p>}

      <div className="row row--tight super-skill__actions">
        <span className="spacer" />
        <button type="button" className="btn btn--primary" onClick={use} disabled={!able || !body}>
          <Sparkles size={14} aria-hidden /> {t('superSkills.use')}
        </button>
      </div>
    </div>
  )
}

function Field({
  field,
  value,
  onChange,
  desktop,
  pickFile,
  invalid,
}: {
  field: SuperSkillField
  value: string
  onChange: (value: string) => void
  desktop: boolean
  pickFile: (extensions: string[]) => Promise<string | null>
  invalid: boolean
}) {
  const { t, locale } = useI18n()
  const label = local(field.label, locale)
  const hint = local(field.hint, locale)
  const id = `super-skill-${field.key}`

  if (field.type === 'toggle') {
    return (
      <label className="row row--tight small super-skill__toggle">
        <input type="checkbox" checked={value === 'true'} onChange={(e) => onChange(e.target.checked ? 'true' : 'false')} aria-invalid={invalid || undefined} />
        <span>
          {label}
          {hint && <span className="field__hint" style={{ display: 'block' }}>{hint}</span>}
        </span>
      </label>
    )
  }

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>{label}</label>
      {field.type === 'select' ? (
        <select id={id} className="select" value={value} onChange={(e) => onChange(e.target.value)}>
          {(field.options ?? []).map((o) => (
            <option key={o.value} value={o.value}>{local(o.label, locale)}</option>
          ))}
        </select>
      ) : field.type === 'longtext' ? (
        <textarea id={id} className="input super-skill__text" rows={3} maxLength={MAX_FIELD} value={value} placeholder={hint || undefined} aria-invalid={invalid || undefined} onChange={(e) => onChange(e.target.value)} />
      ) : field.type === 'source' ? (
        <div className="row row--tight">
          <input id={id} className="input" value={value} maxLength={MAX_FIELD} placeholder={desktop ? t('superSkills.sourcePlaceholder') : t('superSkills.sourcePlaceholderWeb')} aria-invalid={invalid || undefined} onChange={(e) => onChange(e.target.value)} />
          {desktop && (
            <button
              type="button"
              className="btn"
              onClick={() => void pickFile((field.accept ?? '').split(',').filter(Boolean)).then((path) => path && onChange(path)).catch(() => {})}
            >
              <FolderOpen size={14} aria-hidden /> {t('superSkills.pickFile')}
            </button>
          )}
        </div>
      ) : (
        <input id={id} className="input" type={field.type === 'number' ? 'number' : 'text'} value={value} maxLength={MAX_FIELD} placeholder={field.type === 'text' ? hint || undefined : undefined} aria-invalid={invalid || undefined} onChange={(e) => onChange(e.target.value)} />
      )}
      {hint && field.type !== 'longtext' && field.type !== 'text' && <span className="field__hint">{hint}</span>}
      {invalid && <span className="field__error">{t('superSkills.required')}</span>}
    </div>
  )
}
